import type { WellIndexType } from '@go-widgets/well-logs-widget';
import { BackLight, MenuLightCustom } from '@repo/go-icons';
import { Button, Loader, Tooltip } from '@repo/go-ui';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useStore } from 'src/app/root-store';
import { useService } from 'src/packages/di';
import { CreateTabModal } from 'src/pages/dashboard/features/create-tab-modal/CreateTabModal';
import { RemoveObjectModal } from 'src/pages/dashboard/features/remove-object-modal/RemoveObjectModal';
import { TabsList } from 'src/pages/dashboard/features/tabs/TabsList';
import { Toolbar } from 'src/pages/dashboard/features/toolbar/Toolbar';
import { Workbench } from 'src/pages/dashboard/features/workbench/Workbench';

import { AlertNotificationsContainer } from '../anomaly-notifications-service/alert-notifications';
import { ConnectionClosedModal } from '../connection-closed-modal/ConnectionClosedModal';
import { NewContentWidgetModal } from '../new-content-widget-modal/NewContentWidgetModal';
import { RetryingTabsRecoveringModal } from '../retrying-tabs-recovering-modal/RetryingTabsRecoveringModal';
import { SaveTabAsTemplateModal } from '../save-tab-as-template-modal/SaveTabAsTemplateModal';
import { ScreenFlickerOverlay } from '../screen-flicker/ScreenFlickerOverlay';
import { SessionsConflictModal } from '../sessions-conflict-modal/SessionsConflictModal';
import { TabsRecoveringModal } from '../tabs-recovering-modal/TabsRecoveringModal';
import { RightToolbar } from '../toolbar/RightToolbar';

import styles from './Workspace.module.scss';
import { WorkspaceStore } from './Workspace.store';
import { MASTER_ID_KEY } from './Workspace.utils';

export const Workspace = observer(function Workspace() {
  const [workspaceStore] = useState(() => new WorkspaceStore());
  const { sidebar } = useStore();

  const { t } = useTranslation();

  const {
    isWorkspaceInitProcess,
    isLoading,
    isUpdating,
    hasError,
    actualTab,
    onRemoveTabCancel,
    onRemoveTabAccept,
    openWellListWidget,
    isRemoveTabModalOpen,
    onCreateNewTabAccept,
    onCreateNewEmptyTab,
    onCreateNewTabCancel,
    isCreateTabModalOpen,
    isSaveTabAsTemplateModalOpened,
    onSaveTabAsTemplateModalClose,
    onSaveTabAsTemplateModalOpen,
    onSaveTabAsTemplate,
    onUpdateTemplate,
    setTabSavedAsTemplate,
    onDragEnd,
    createTab,
    removeTab,
    changeTabName,
    changeActiveKey,
    onCreateNewContentWidget,
    newContentWidgetController,
    externalTabsIds: disabledTabs,
    onRecoverExternalTabs,
    externalTabsRecoveringController,
    onExternalTabsRecoveringCancel,
    externalTabsRetryRecoveringController,
    onRetryExternalTabsRecovering,
    onRetryExternalTabsRecoveringCancel,
    tabsEntities,
    masterWindowsManager,
    sessionManager,
    isExtraMasterDetected,
    effect,
  } = workspaceStore;

  const {
    sessionRegistrationService: { remoteSessionController, onCloseCurrentSession, onKeepCurrentSession },
  } = sessionManager;

  const { activeKey } = workspaceStore.workspace;
  const workspaceRef = useRef<HTMLDivElement>(null);
  const { isFullscreenMode, enterFullscreen, exitFullscreen } = useService('fullscreenService');
  const { status } = useService('sessionService');
  const authService = useService('authService');

  useEffect(effect, [effect]);

  useEffect(() => {
    workspaceStore.localStorageService.set(MASTER_ID_KEY, workspaceStore.id);
  }, [workspaceStore]);

  useEffect(() => {
    if (workspaceStore.isWorkspaceInitProcess) {
      return;
    }

    return workspaceStore.hintsStore.init();
  }, [workspaceStore, workspaceStore.isWorkspaceInitProcess]);

  if (hasError) {
    return (
      <main className={styles.wrapper}>
        <p>Error. Please reload the page</p>
      </main>
    );
  }

  if (isExtraMasterDetected) {
    return (
      <ConnectionClosedModal
        isOpened={isExtraMasterDetected}
        titleT="modals:MainWindowInstanceDuplication.title"
        descT="modals:MainWindowInstanceDuplication.desc"
      />
    );
  }

  const isConnectionClosed = status === 'closed';

  if (isConnectionClosed) {
    return (
      <ConnectionClosedModal
        isOpened={isConnectionClosed}
        titleT="modals:ClosedSession.title"
        descT="modals:ClosedSession.desc"
        externalAction={authService.logout}
        externalActionT="modals:ClosedSession.bactToLogin"
        externalActionIcon={<BackLight />}
      />
    );
  }

  if (status === 'error') {
    return (
      <SessionsConflictModal
        onCancel={onCloseCurrentSession}
        onConfirm={onKeepCurrentSession}
        isOpened={!!remoteSessionController}
      />
    );
  }

  if (isLoading || isWorkspaceInitProcess) {
    return <Loader size="l" className={styles.loader} />;
  }

  const handleEnterFullscreen = () => {
    enterFullscreen(document.documentElement);
  };

  const onCreateWidget = (type: string, wellIndexType?: WellIndexType) => {
    newContentWidgetController?.resolve({ type, wellIndexType });
  };

  const onCloseNewContentWidgetModal = () => {
    newContentWidgetController?.resolve(null);
  };

  return (
    <main className={clsx(styles.wrapper, actualTab?.fullscreenWidget && styles.wrapperFullscreenMode)}>
      {!isFullscreenMode && (
        <section className={styles.header}>
          <Tooltip title={t('hints:openSideMenu')} useSafePolygon={false}>
            <Button
              variant="flat"
              icon={<MenuLightCustom className={styles.menuButton__icon} />}
              className={styles.menuButton}
              onClick={sidebar.open}
            />
          </Tooltip>
          <Toolbar
            workspaceStore={workspaceStore}
            workspaceHintsStore={workspaceStore.hintsStore}
            disabledTabs={disabledTabs}
            onSaveTabAsTemplateModalOpen={onSaveTabAsTemplateModalOpen}
            onUpdateTemplate={onUpdateTemplate}
            onTabSavedAsTemplateChange={setTabSavedAsTemplate}
            onOpenInNewWindow={masterWindowsManager?.openWindow}
            activeKey={activeKey}
            onFullscreen={handleEnterFullscreen}
            onSetActiveKey={changeActiveKey}
            onCreateTab={createTab}
            onRemoveTab={removeTab}
            onTabClose={masterWindowsManager?.closeTab}
            onTabFocus={masterWindowsManager?.focusTab}
            onChangeTabName={changeTabName}
            tabsEntities={tabsEntities}
            actualTab={actualTab}
            onCreateContentWidget={onCreateNewContentWidget}
            onCreateWellListWidget={openWellListWidget}
            onCreateWellsFilterWidget={actualTab?.createWellsFilterWidget}
            onCreateWellLogsManagerWidget={actualTab?.createWellLogsManager}
          />
          <TabsList
            workspaceHintsStore={workspaceStore.hintsStore}
            getIsTabDisabled={(tab) => !!disabledTabs.find((item) => item === tab.id)}
            onWindowOpen={masterWindowsManager?.openWindow}
            activeKey={activeKey}
            onSetActiveKey={changeActiveKey}
            onDragEnd={onDragEnd}
            onCreateTab={createTab}
            onRemoveTab={removeTab}
            onCloseTab={masterWindowsManager?.closeTab}
            onTabFocus={masterWindowsManager?.focusTab}
            onChangeTabName={changeTabName}
            tabsEntities={tabsEntities}
            onSaveAsTemplate={(tab) => {
              setTabSavedAsTemplate(tab);
              onSaveTabAsTemplateModalOpen();
            }}
          />
          <RightToolbar
            workspaceStore={workspaceStore}
            workspaceHintsStore={workspaceStore.hintsStore}
            disabledTabs={disabledTabs}
            onSaveTabAsTemplateModalOpen={onSaveTabAsTemplateModalOpen}
            onUpdateTemplate={onUpdateTemplate}
            onTabSavedAsTemplateChange={setTabSavedAsTemplate}
            onOpenInNewWindow={masterWindowsManager?.openWindow}
            activeKey={activeKey}
            onFullscreen={handleEnterFullscreen}
            onSetActiveKey={changeActiveKey}
            onCreateTab={createTab}
            onRemoveTab={removeTab}
            onTabClose={masterWindowsManager?.closeTab}
            onTabFocus={masterWindowsManager?.focusTab}
            onChangeTabName={changeTabName}
            tabsEntities={tabsEntities}
            actualTab={actualTab}
            onCreateContentWidget={onCreateNewContentWidget}
            onCreateWellListWidget={openWellListWidget}
            onCreateWellsFilterWidget={actualTab?.createWellsFilterWidget}
            onCreateWellLogsManagerWidget={actualTab?.createWellLogsManager}
          />
        </section>
      )}

      {(isUpdating || masterWindowsManager?.isTabsSyncModalOpen) && <Loader className={styles.loader} />}

      {actualTab && (
        <Workbench
          key={actualTab.id}
          isFullscreenMode={isFullscreenMode}
          tab={actualTab}
          workspaceRef={workspaceRef}
          onExitFullscreen={exitFullscreen}
        />
      )}

      {actualTab && (
        <NewContentWidgetModal
          isOpened={!!newContentWidgetController}
          onCreateWidget={onCreateWidget}
          actualTab={actualTab}
          onCloseModal={onCloseNewContentWidgetModal}
        />
      )}

      <RemoveObjectModal
        title={t('modals:RemoveTabModal.title')}
        desc={t('modals:RemoveTabModal.desc')}
        onCancel={onRemoveTabCancel}
        onConfirm={onRemoveTabAccept}
        isOpened={isRemoveTabModalOpen}
      />

      <CreateTabModal
        workspaceHintsStore={workspaceStore.hintsStore}
        onCancel={onCreateNewTabCancel}
        onConfirm={onCreateNewTabAccept}
        onCreateEmptyTab={onCreateNewEmptyTab}
        isOpened={isCreateTabModalOpen}
      />

      <TabsRecoveringModal
        isOpened={!!externalTabsRecoveringController}
        onConfirm={onRecoverExternalTabs}
        onCancel={onExternalTabsRecoveringCancel}
      />

      <RetryingTabsRecoveringModal
        isOpened={!!externalTabsRetryRecoveringController}
        onConfirm={onRetryExternalTabsRecovering}
        onCancel={onRetryExternalTabsRecoveringCancel}
      />

      {actualTab && (
        <SaveTabAsTemplateModal
          tabName={actualTab.name}
          isOpened={isSaveTabAsTemplateModalOpened}
          onClose={onSaveTabAsTemplateModalClose}
          onSave={onSaveTabAsTemplate}
        />
      )}
      <ScreenFlickerOverlay />
      <AlertNotificationsContainer />
    </main>
  );
});
